/**
 * A {@link Preloader} subclass that transparently caches 1:1 (`belongsTo`/`hasOne`)
 * relation loads through an externally-supplied cache service, keyed canonically
 * by the related row's identity and tagged for write-through invalidation.
 *
 * Both relation-loading paths funnel through Lucid's `Preloader`
 * (`processRelation` for a single parent, `processRelationForMany` for eager
 * loads across many parents), so overriding them here covers `model.load()` and
 * `Model.query().preload()` alike (ADR-001).
 */

import { Preloader } from '@adonisjs/lucid/orm'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { runCacheMiddleware } from './cache_middleware'
import { deepCloneSnapshot } from './deep_clone_snapshot'
import { reportRelationCacheError } from './cache_error_handler'
import { reportRelationCacheEvent } from './cache_event_handler'
import { decodeCacheValue, encodeCacheValue } from './cache_codec'
import { resolveRelationsCacheService, registerRelationCacheTarget } from './cache_service_resolver'
import {
  E_RELATION_CACHE_KEY_DIVERGENCE,
  E_INVALID_RESOURCEFUL_MIXIN_OPTIONS,
  E_RELATION_CACHE_READ_FAILED,
  E_RELATION_CACHE_WRITE_FAILED,
  E_RELATION_CACHE_RESOLUTION_FAILED,
  E_RELATION_CACHE_REHYDRATION_FAILED,
} from '@nhtio/lucid-resourceful/errors'
import type { CacheMiddlewarePipeline } from './cache_middleware'
import type { LucidModel, LucidRow } from '@adonisjs/lucid/types/model'
import type { RelationCacheEvent, RelationCacheEventContext } from './cache_event_handler'
import type { CacheServiceLike, RelationCacheEntryOptions } from './cache_service_resolver'

/**
 * Per-call cache controls recorded by `preloadCached(name, options, cb?)`.
 * @internal
 */
export interface RelationCacheCallOptions extends RelationCacheEntryOptions {
  /** Bypass the cache read but still write the fresh result back. */
  forceLoad?: boolean
}

/**
 * The eager query builder returned by `relation.eagerQuery(...)`, narrowed to the
 * chained methods the cache invokes at the chokepoint.
 * @internal
 */
interface EagerQueryLike {
  debug: (enabled: boolean) => EagerQueryLike
  sideload: (values: Record<string, unknown>) => EagerQueryLike
  selectRelationKeys: () => EagerQueryLike
  exec: () => Promise<LucidRow[]>
}

/**
 * A relation booted by Lucid, narrowed to the metadata the cache reads.
 * @internal
 */
interface BootedRelationLike {
  type: string
  relationName: string
  foreignKey: string
  localKey: string
  /** Whether Lucid has booted this relation (keys populated). */
  booted?: boolean
  /** Boots the relation, populating `foreignKey`/`localKey`. Idempotent in Lucid. */
  boot?: () => void
  relatedModel: () => LucidModel
  eagerQuery: (parent: LucidRow | LucidRow[], client: unknown) => EagerQueryLike
  setRelated: (parent: LucidRow, related: LucidRow | null) => void
  setRelatedForMany: (parent: LucidRow[], related: LucidRow[]) => void
}

/**
 * A `preload(name, callback?)` call captured from a relation-load callback while
 * classifying it as nesting-only (see {@link CachingPreloader.#classifyCallback}).
 * @internal
 */
interface CapturedNestedPreload {
  name: string
  callback?: (query: unknown) => void
}

/**
 * The narrow slice of a sibling {@link CachingPreloader} the cascade driver uses
 * to load nested relations onto already-resolved related rows (typed loosely to
 * avoid Lucid's generic relation-name machinery for a dynamic relation name).
 * @internal
 */
interface NestedCascadeDriver {
  preload: (name: string, callback?: (query: unknown) => void) => unknown
  sideload: (values: Record<string, unknown>) => unknown
  processAllForMany: (rows: LucidRow[], client: unknown) => Promise<void>
}

/**
 * The runtime shape of the base {@link Preloader}'s state and chokepoint methods.
 *
 * Lucid declares `model`/`preloads`/`sideloaded`/`debugQueries` and the
 * `processRelation*` methods as `private` in its `.d.ts`, but they exist as
 * ordinary public members at runtime. This view exposes them in a type-safe way
 * so the subclass can read base state and delegate the uncached fallback to the
 * base implementations without `any`.
 * @internal
 */
interface BasePreloaderInternals {
  model: LucidModel
  preloads: Record<string, { relation: unknown; callback?: unknown }>
  sideloaded: Record<string, unknown>
  debugQueries: boolean
  processRelation: (name: string, parent: LucidRow, client: unknown) => Promise<void>
  processRelationForMany: (name: string, parents: LucidRow[], client: unknown) => Promise<void>
}

/**
 * Models whose `relationsCache` keys have already passed relation-name
 * validation. Keyed by the model constructor so a misconfigured model is never
 * recorded (it throws before reaching the `add`) and so entries are GC'd with
 * the model. Exists to keep the per-query hot path cheap: `registerCacheTargets`
 * runs on every `new CachingPreloader`, but the Joi key check runs at most once
 * per model.
 * @internal
 */
const relationKeysValidatedModels = new WeakSet<LucidModel>()

/**
 * Caching extension of Lucid's `Preloader`.
 * @internal
 */
export class CachingPreloader extends Preloader {
  /** Pre-merged per-relation entry options (relation name → merged options). */
  #relationsCache: Map<string, RelationCacheEntryOptions>
  /** Per-call overrides recorded via `preloadCached`, cleared as they are consumed. */
  #callOptions: Map<string, RelationCacheCallOptions>

  constructor(
    model: LucidModel,
    relationsCache: Map<string, RelationCacheEntryOptions>,
    callOptions: Map<string, RelationCacheCallOptions> = new Map()
  ) {
    super(model)
    this.#relationsCache = relationsCache
    this.#callOptions = callOptions
    CachingPreloader.registerCacheTargets(model)

    // Lucid declares `processRelation`/`processRelationForMany` as `private` in
    // its types (so a TS-level `override` is rejected) but invokes them
    // polymorphically at runtime via `this.processRelation(...)`. We install our
    // caching implementations as own instance properties, shadowing the base
    // prototype methods at the runtime chokepoint without a forbidden override.
    const internals = this as unknown as BasePreloaderInternals
    internals.processRelation = (name, parent, client) =>
      this.#cachedProcessRelation(name, parent, client)
    internals.processRelationForMany = (name, parents, client) =>
      this.#cachedProcessRelationForMany(name, parents, client)
  }

  /*
  |--------------------------------------------------------------------------
  | Static properties
  |--------------------------------------------------------------------------
  */

  /**
   * Reads the resourceful name of a model, falling back to its class name when
   * the model is not resourceful (cache keys/tags must still be deterministic).
   *
   * @param model - The model whose identity name is needed.
   * @returns The canonical model name used in cache keys and tags.
   */
  static #modelName(model: LucidModel): string {
    const resourcefulName = (model as LucidModel & { $resourcefulName?: string }).$resourcefulName
    return resourcefulName ?? model.name
  }

  /**
   * Determines whether a relation is a cacheable 1:1 inline-loadable type.
   *
   * @param relation - The booted relation to classify.
   * @returns `true` for `belongsTo`/`hasOne`, `false` otherwise.
   */
  static #isCacheable(relation: BootedRelationLike): boolean {
    return relation.type === 'belongsTo' || relation.type === 'hasOne'
  }

  /**
   * Ensures a relation is booted so its `localKey`/`foreignKey` are populated.
   * Lucid populates these only in `boot()`, and reading them on an unbooted
   * relation yields `undefined`. `boot()` is idempotent in Lucid.
   *
   * @param relation - The relation to boot.
   */
  static #ensureBooted(relation: BootedRelationLike): void {
    if (relation.booted === false && typeof relation.boot === 'function') {
      relation.boot()
    }
  }

  /**
   * Fails loud when a `belongsTo` relation's `localKey` differs from the related
   * model's `primaryKey`. The cache keys a `belongsTo` lookup by the parent FK
   * value (the related row's `localKey`) but tags the stored entry by the related
   * row's `primaryKey`; when these diverge, lookup key and eviction tag describe
   * different rows and a cached entry could serve the wrong row — so we refuse to
   * cache it rather than risk incorrect data.
   *
   * @param relation - The booted relation to validate.
   * @throws {E_RELATION_CACHE_KEY_DIVERGENCE} When a belongsTo `localKey !== primaryKey`.
   */
  static #assertBelongsToKeyConsistency(relation: BootedRelationLike): void {
    if (relation.type !== 'belongsTo') {
      return
    }
    CachingPreloader.#ensureBooted(relation)
    const relatedModel = relation.relatedModel()
    if (relation.localKey === relatedModel.primaryKey) {
      return
    }
    throw new E_RELATION_CACHE_KEY_DIVERGENCE(
      relation.relationName,
      CachingPreloader.#modelName(relatedModel),
      relation.localKey,
      relatedModel.primaryKey
    )
  }

  /**
   * Builds the canonical lookup key for a single parent's 1:1 relation.
   *
   * `belongsTo` keys by the related PK held on the parent FK (known pre-load);
   * `hasOne` keys by its defining FK against the parent local-key value.
   *
   * @param relation - The booted 1:1 relation.
   * @param parent - The parent row the relation hangs off.
   * @returns The lookup key, or `null` when the parent lacks the needed identity value.
   */
  static #lookupKeyForParent(relation: BootedRelationLike, parent: LucidRow): string | null {
    const relatedName = CachingPreloader.#modelName(relation.relatedModel())

    if (relation.type === 'belongsTo') {
      CachingPreloader.#assertBelongsToKeyConsistency(relation)
      const relatedPrimaryKeyValue = (parent as unknown as Record<string, unknown>)[
        relation.foreignKey
      ]
      if (relatedPrimaryKeyValue === null || relatedPrimaryKeyValue === undefined) {
        return null
      }
      return `${relatedName}:${String(relatedPrimaryKeyValue)}`
    }

    const parentLocalKeyValue = (parent as unknown as Record<string, unknown>)[relation.localKey]
    if (parentLocalKeyValue === null || parentLocalKeyValue === undefined) {
      return null
    }
    return `${relatedName}:${relation.foreignKey}=${String(parentLocalKeyValue)}`
  }

  /**
   * Builds the canonical eviction tag for a loaded related row — its own
   * identity `{RelatedModelName}:{relatedPk}`, used by write-through eviction.
   *
   * @param relation - The booted 1:1 relation.
   * @param relatedRow - The loaded related row.
   * @returns The identity tag, or `null` when the row's PK is unavailable.
   */
  static #identityTagForRow(relation: BootedRelationLike, relatedRow: LucidRow): string | null {
    const relatedModel = relation.relatedModel()
    const relatedName = CachingPreloader.#modelName(relatedModel)
    const primaryKeyValue = (relatedRow as unknown as Record<string, unknown>)[
      relatedModel.primaryKey
    ]
    if (primaryKeyValue === null || primaryKeyValue === undefined) {
      return null
    }
    return `${relatedName}:${String(primaryKeyValue)}`
  }

  /**
   * Rebuilds a related model instance from a cached, consumed-`$attributes`
   * snapshot, mirroring Lucid's own post-fetch construction (`$setAttribute`
   * per field + `$hydrateOriginals` + `$isPersisted`/`$isLocal`) so the result
   * is a clean (`$isDirty === false`), persisted, fully-operating instance
   * (ADR-008).
   *
   * @param relatedModel - The model constructor to instantiate.
   * @param snapshot - The cached `$attributes` snapshot.
   * @param sideloaded - The sideloaded attributes to re-apply.
   * @returns The rehydrated related row.
   */
  static #rehydrate(
    relatedModel: LucidModel,
    snapshot: Record<string, unknown>,
    sideloaded: Record<string, unknown>
  ): LucidRow {
    const row = new relatedModel() as LucidRow
    for (const [attribute, value] of Object.entries(snapshot)) {
      row.$setAttribute(attribute, value)
    }
    row.$sideloaded = { ...sideloaded }
    row.$hydrateOriginals()
    row.$isPersisted = true
    row.$isLocal = false
    return row
  }

  /**
   * Runs the related model's `cacheMiddlewarePrepare` pipeline over a snapshot and
   * encodes the result for storage (ADR-011/012). Throws on a middleware or encoder
   * failure so the caller routes it to `E_RELATION_CACHE_WRITE_FAILED` and skips the
   * write — the row was already set on the parent, so the read still succeeds (ADR-009).
   *
   * @param relatedModel - The model whose instance is being cached.
   * @param name - The relation name (for the middleware context).
   * @param lookupKey - The canonical cache key (for the middleware context).
   * @param snapshot - The consumed-`$attributes` snapshot to prepare and encode.
   * @returns The encoded cache value to store.
   */
  static async #prepareForStore(
    relatedModel: LucidModel,
    name: string,
    lookupKey: string,
    snapshot: Record<string, unknown>
  ): Promise<string> {
    const pipeline = (
      relatedModel as LucidModel & {
        $resourcefulCacheMiddlewarePrepare?: CacheMiddlewarePipeline | null
      }
    ).$resourcefulCacheMiddlewarePrepare
    const prepared = await runCacheMiddleware(pipeline ?? null, {
      phase: 'prepare',
      model: relatedModel,
      relation: name,
      key: lookupKey,
      value: snapshot,
    })
    return encodeCacheValue(prepared)
  }

  /**
   * Decodes a cached entry and runs the related model's `cacheMiddlewareConsume`
   * pipeline over it (ADR-011/012), yielding a snapshot ready for `#rehydrate`.
   * Throws on a decode or middleware failure so the caller treats the entry as
   * poisoned — reported as `E_RELATION_CACHE_REHYDRATION_FAILED` and served as a
   * miss (ADR-009).
   *
   * @param relatedModel - The model the cached snapshot belongs to.
   * @param name - The relation name (for the middleware context).
   * @param lookupKey - The canonical cache key (for the middleware context).
   * @param encoded - The encoded cache value read from the cache service.
   * @returns The decoded, consumed snapshot.
   */
  static async #consumeFromCache(
    relatedModel: LucidModel,
    name: string,
    lookupKey: string,
    encoded: string
  ): Promise<Record<string, unknown>> {
    const snapshot = decodeCacheValue(encoded)
    const pipeline = (
      relatedModel as LucidModel & {
        $resourcefulCacheMiddlewareConsume?: CacheMiddlewarePipeline | null
      }
    ).$resourcefulCacheMiddlewareConsume
    return runCacheMiddleware(pipeline ?? null, {
      phase: 'consume',
      model: relatedModel,
      relation: name,
      key: lookupKey,
      value: snapshot,
    })
  }

  /**
   * Classifies a relation-load callback as **nesting-only** (it calls only
   * `preload`/`load`, which add child relations without changing which parent rows
   * match) versus **filtering** (it touches anything else — `where`, `select`,
   * `limit`, …, which shapes the matched set).
   *
   * A filtering callback means the canonical cached row can't be trusted to satisfy
   * the predicate, so the caller must bypass the cache. A nesting-only callback is
   * safe to serve from cache, and its captured child preloads are cascaded onto the
   * resolved rows — each caching at its own identity (AC line 33 / ADR-002).
   *
   * Runs the callback against a recording Proxy and is **default-deny**: ANY access
   * other than `preload`/`load` (or a throw) marks it filtering. A filtering
   * callback therefore can NEVER be misclassified as cacheable; the only failure
   * mode is a nesting-only callback conservatively treated as filtering — i.e. the
   * pre-existing bypass, a perf (not correctness) outcome.
   *
   * @param callback - The relation-load callback to classify.
   * @returns `nestingOnly` plus the captured child preloads (name + optional nested callback).
   */
  static #classifyCallback(callback: (query: unknown) => void): {
    nestingOnly: boolean
    preloads: CapturedNestedPreload[]
  } {
    const preloads: CapturedNestedPreload[] = []
    let filtering = false
    const recorder: unknown = new Proxy(
      {},
      {
        get(_target, property) {
          if (property === 'preload' || property === 'load') {
            return (name: string, nestedCallback?: (query: unknown) => void) => {
              preloads.push({ name, callback: nestedCallback })
              return recorder
            }
          }
          // Any other string-keyed interaction shapes the result set → not safe to
          // serve from the canonical cache. Return a chaining no-op so the callback
          // can keep calling without throwing (symbol/protocol reads are ignored).
          if (typeof property === 'string') {
            filtering = true
          }
          return () => recorder
        },
      }
    )
    try {
      callback(recorder)
    } catch {
      // A callback that throws against the recorder is treated as unsafe to cache.
      filtering = true
    }
    return { nestingOnly: !filtering, preloads }
  }

  /**
   * Validates — once per model — that every key in the model's `relationsCache`
   * names a relation that actually exists on the model, rejecting typos and stale
   * names that would otherwise be silently skipped (and never cached). Mirrors the
   * mixin's Joi approach: a strict object schema whose only permitted keys are the
   * model's registered relation names; any other configured key fails validation.
   *
   * The relation VALUES (BentoCache entry options) are validated separately at
   * mixin declare time. KEY validation has to happen here instead, because a
   * model's relations are only registered by its decorators AFTER the mixin's
   * option schema runs — so at declare time there are no relations to check the
   * keys against. By the time `registerCacheTargets` runs (host boot, or the first
   * `.query()`/`.load()`), the decorators have populated `$relationsDefinitions`.
   * Memoized via {@link relationKeysValidatedModels} so the per-query hot path
   * pays the Joi cost at most once per model.
   *
   * @param model - The model declaring the relations.
   * @param relationsCache - The model's pre-merged relation cache config.
   * @throws {E_INVALID_RESOURCEFUL_MIXIN_OPTIONS} When a key is not a known relation name.
   */
  static #assertRelationsCacheKeysAreRelations(
    model: LucidModel,
    relationsCache: Map<string, RelationCacheEntryOptions>
  ): void {
    if (relationKeysValidatedModels.has(model)) {
      return
    }
    const allowedRelationKeys: Record<string, ReturnType<typeof joi.any>> = {}
    for (const relationName of model.$relationsDefinitions.keys()) {
      allowedRelationKeys[relationName] = joi.any()
    }
    const configuredKeys: Record<string, true> = {}
    for (const relationName of relationsCache.keys()) {
      configuredKeys[relationName] = true
    }
    const { error } = joi.object(allowedRelationKeys).validate(configuredKeys, {
      abortEarly: false,
    })
    if (error) {
      throw new E_INVALID_RESOURCEFUL_MIXIN_OPTIONS(CachingPreloader.#modelName(model), error)
    }
    relationKeysValidatedModels.add(model)
  }

  /**
   * Eagerly registers every configured cacheable 1:1 relation's related-model
   * identity name as a relation-cache TARGET (and validates `belongsTo` key
   * consistency up front). Registering from the DECLARED config ensures the
   * write-through eviction gate knows the full target set. Before registering, it
   * validates (once per model) that every `relationsCache` key is a real relation
   * name (see {@link CachingPreloader.#assertRelationsCacheKeysAreRelations}).
   *
   * IMPORTANT: this is also invoked process-globally at host boot (via the
   * exported `registerRelationCacheTargetsForModel`), NOT only from a constructed
   * preloader — a preloader is built lazily on first `.query()`/`.load()` of the
   * declaring parent, which a credential/rate worker process may never call. Boot
   * registration makes the target set process-global so a "cold" writer (e.g. a
   * rates-sync command that only ever updates `Currency`) still evicts shared L2
   * entries another process cached.
   *
   * @param model - The model declaring the relations (reads `$resourcefulRelationsCache`).
   */
  static registerCacheTargets(model: LucidModel): void {
    const relationsCache = (
      model as LucidModel & { $resourcefulRelationsCache?: Map<string, RelationCacheEntryOptions> }
    ).$resourcefulRelationsCache
    if (!relationsCache || relationsCache.size === 0) {
      return
    }
    CachingPreloader.#assertRelationsCacheKeysAreRelations(model, relationsCache)
    for (const relationName of relationsCache.keys()) {
      // `$getRelation` throws on an unknown relation name; a misconfigured entry
      // must not break registration, so resolve it defensively.
      let relation: BootedRelationLike | undefined
      try {
        relation = model.$getRelation(relationName) as unknown as BootedRelationLike | undefined
      } catch {
        relation = undefined
      }
      if (!relation || !CachingPreloader.#isCacheable(relation)) {
        continue
      }
      // `localKey`/`foreignKey` are only populated after the relation is booted;
      // boot it so the belongsTo key-consistency guard reads real keys, not undefined.
      CachingPreloader.#ensureBooted(relation)
      CachingPreloader.#assertBelongsToKeyConsistency(relation)
      registerRelationCacheTarget(CachingPreloader.#modelName(relation.relatedModel()))
    }
  }

  /*
  |--------------------------------------------------------------------------
  | Instance properties
  |--------------------------------------------------------------------------
  */

  /**
   * Type-safe view of the base preloader's runtime state and chokepoint methods
   * (declared `private` in Lucid's types but public at runtime).
   *
   * @returns This instance viewed as {@link BasePreloaderInternals}.
   */
  get #internals(): BasePreloaderInternals {
    return this as unknown as BasePreloaderInternals
  }

  /**
   * Emits a relation-cache lifecycle event to the host's `onRelationsCacheEvent`
   * hook (per-model → process-wide default → no-op). Synchronous, guarded, and
   * never throws into a load — observability is the host's concern, so the
   * package imports no logger of its own.
   *
   * @param event - The cache lifecycle event that occurred.
   * @param context - Structured context for the event.
   */
  #emit(event: RelationCacheEvent, context: RelationCacheEventContext): void {
    reportRelationCacheEvent(this.#internals.model, event, context)
  }

  /**
   * Delegates a single-parent load to the base (uncached) implementation.
   *
   * @param name - The relation name.
   * @param parent - The parent row.
   * @param client - The query client.
   */
  #superProcessRelation(name: string, parent: LucidRow, client: unknown): Promise<void> {
    const base = Preloader.prototype as unknown as BasePreloaderInternals
    return base.processRelation.call(this, name, parent, client)
  }

  /**
   * Delegates a many-parent eager load to the base (uncached) implementation.
   *
   * @param name - The relation name.
   * @param parents - The parent rows.
   * @param client - The query client.
   */
  #superProcessRelationForMany(name: string, parents: LucidRow[], client: unknown): Promise<void> {
    const base = Preloader.prototype as unknown as BasePreloaderInternals
    return base.processRelationForMany.call(this, name, parents, client)
  }

  /**
   * Resolves the merged entry options for a relation by layering any recorded
   * per-call overrides over the pre-merged model-level options.
   *
   * @param name - The relation name.
   * @returns The effective entry options for this load.
   */
  #entryOptions(name: string): RelationCacheEntryOptions {
    const base = this.#relationsCache.get(name) ?? {}
    const call = this.#callOptions.get(name)
    if (!call) {
      return base
    }
    // `forceLoad` is a read-time control, not a cache entry option, so strip it
    // before merging the per-call overrides over the model-level defaults.
    const overrides: RelationCacheEntryOptions = {
      ttl: call.ttl,
      grace: call.grace,
      graceBackoff: call.graceBackoff,
      suppressL2Errors: call.suppressL2Errors,
      timeout: call.timeout,
      hardTimeout: call.hardTimeout,
      lockTimeout: call.lockTimeout,
    }
    const merged: RelationCacheEntryOptions = { ...base }
    for (const [optionKey, optionValue] of Object.entries(overrides)) {
      if (optionValue !== undefined) {
        ;(merged as Record<string, unknown>)[optionKey] = optionValue
      }
    }
    return merged
  }

  /**
   * Resolves the cache service for this preloader's model. Reads fail loud: a
   * resolver that throws propagates (ADR-007).
   *
   * @returns The resolved cache service, or `null` when caching is opted out.
   */
  #resolveCache(): Promise<CacheServiceLike | null> {
    const model = this.#internals.model
    const resolver = (
      model as LucidModel & {
        $resourcefulRelationsCacheService?: () =>
          | CacheServiceLike
          | null
          | Promise<CacheServiceLike | null>
      }
    ).$resourcefulRelationsCacheService
    return resolveRelationsCacheService(model, resolver)
  }

  /**
   * Processes a single-parent relation load, serving from cache when the
   * relation is a configured 1:1 type and falling back to `super` otherwise.
   *
   * @param name - The relation name being loaded.
   * @param parent - The parent row.
   * @param client - The query client to use for the eager query.
   */
  async #cachedProcessRelation(name: string, parent: LucidRow, client: unknown): Promise<void> {
    // The recorded per-call options are read once up front and cleared on EVERY
    // exit path via `finally`, so a bypass/early return can't leak a stale
    // forceLoad/ttl into a later same-name load on this preloader.

    try {
      const internals = this.#internals
      const model = internals.model
      const relation = model.$getRelation(name) as unknown as BootedRelationLike | undefined
      const callOptions = this.#callOptions.get(name)
      const hasCallback = typeof internals.preloads[name]?.callback === 'function'

      // A relation that is not configured, not a cacheable 1:1 type, or is being
      // filtered by a callback bypasses the cache entirely (calls super unchanged).
      if (
        !relation ||
        !this.#relationsCache.has(name) ||
        !CachingPreloader.#isCacheable(relation)
      ) {
        await this.#superProcessRelation(name, parent, client)
        return
      }
      // A callback is classified: a *filtering* callback (`q => q.where(...)`) can't
      // be served from the canonical cache and bypasses; a *nesting-only* callback
      // (`q => q.preload('tenant')`) is safe — we cache the outer row and cascade the
      // captured child preloads, each caching at its own identity (AC line 33 /
      // ADR-002). Classification is default-deny (see `#classifyCallback`).
      let nestedPreloads: CapturedNestedPreload[] | null = null
      if (hasCallback) {
        const callback = internals.preloads[name].callback as (query: unknown) => void
        const classification = CachingPreloader.#classifyCallback(callback)
        if (!classification.nestingOnly) {
          this.#emit('bypass', {
            model: CachingPreloader.#modelName(model),
            relation: name,
          })
          await this.#superProcessRelation(name, parent, client)
          return
        }
        nestedPreloads = classification.preloads
      }

      // Resolving the cache is an operational site (ADR-009): a resolver throw /
      // unreachable app no longer fails the read — it is reported and the load
      // degrades to an uncached DB load (was fail-loud under ADR-007).
      let cache: CacheServiceLike | null
      try {
        cache = await this.#resolveCache()
      } catch (cause) {
        reportRelationCacheError(
          model,
          new E_RELATION_CACHE_RESOLUTION_FAILED(
            { model: CachingPreloader.#modelName(model), relation: name },
            cause
          )
        )
        await this.#superProcessRelation(name, parent, client)
        return
      }

      if (!cache) {
        await this.#superProcessRelation(name, parent, client)
        return
      }

      const lookupKey = CachingPreloader.#lookupKeyForParent(relation, parent)
      if (!lookupKey) {
        // No identity to key by (e.g. null FK) — load normally, nothing to cache.
        await this.#superProcessRelation(name, parent, client)
        return
      }

      const relatedModel = relation.relatedModel()
      const modelName = CachingPreloader.#modelName(model)
      const forceLoad = callOptions?.forceLoad === true

      if (!forceLoad) {
        // Cache read is an operational site (ADR-009): a `get` failure is reported
        // and the load degrades to an uncached DB load.
        let cachedEntry: string | undefined | null
        try {
          cachedEntry = await cache.get<string | undefined | null>({
            key: lookupKey,
          })
        } catch (cause) {
          reportRelationCacheError(
            model,
            new E_RELATION_CACHE_READ_FAILED(
              { model: modelName, relation: name, key: lookupKey },
              cause
            )
          )
          await this.#superProcessRelation(name, parent, client)
          return
        }

        if (cachedEntry) {
          // Decode + consume + rehydration is an operational site (ADR-009): a
          // poisoned/incompatible entry is reported and treated as a miss (fall
          // through to the eager query, which overwrites the bad entry) rather than
          // failing the load.
          let row: LucidRow | null = null
          try {
            const snapshot = await CachingPreloader.#consumeFromCache(
              relatedModel,
              name,
              lookupKey,
              cachedEntry
            )
            row = CachingPreloader.#rehydrate(relatedModel, snapshot, internals.sideloaded)
          } catch (cause) {
            reportRelationCacheError(
              model,
              new E_RELATION_CACHE_REHYDRATION_FAILED(
                { model: modelName, relation: name, key: lookupKey, value: cachedEntry },
                cause
              )
            )
            row = null
          }

          if (row) {
            relation.setRelated(parent, row)
            this.#emit('hit', { model: modelName, relation: name, key: lookupKey })
            // The outer row came from cache (no eager query ran), so load any nested
            // preloads onto it explicitly — each cascades through the cache.
            if (nestedPreloads) {
              await this.#cascadeNested(relatedModel, [row], nestedPreloads, client)
            }
            return
          }
          // Poisoned entry → fall through to the miss path below (refetch + overwrite).
        }
      } else {
        this.#emit('force', { model: modelName, relation: name, key: lookupKey })
      }

      // Miss (or forced): run the normal eager query, set the result, and write back.
      // A nesting callback is NOT applied to this query — its children cascade
      // separately (below) so each caches at its own identity rather than baking
      // into the parent snapshot.
      const query = relation
        .eagerQuery(parent, client)
        .debug(internals.debugQueries)
        .sideload(internals.sideloaded)
      const result = await query.selectRelationKeys().exec()
      const relatedRow = result[0] ?? null
      relation.setRelated(parent, relatedRow)

      if (relatedRow) {
        await this.#store(
          cache,
          name,
          lookupKey,
          relation,
          relatedRow,
          forceLoad ? 'force' : 'miss'
        )
        if (nestedPreloads) {
          await this.#cascadeNested(relatedModel, [relatedRow], nestedPreloads, client)
        }
      }
    } finally {
      this.#callOptions.delete(name)
    }
  }

  /**
   * Processes an eager relation load across many parents: serves cache hits per
   * parent, batches misses into a single `whereIn` query, then writes each
   * fetched row back under its identity key.
   *
   * @param name - The relation name being loaded.
   * @param parents - The parent rows.
   * @param client - The query client to use for the eager query.
   */
  async #cachedProcessRelationForMany(
    name: string,
    parents: LucidRow[],
    client: unknown
  ): Promise<void> {
    // Per-call options are cleared on EVERY exit path via `finally` (see
    // #cachedProcessRelation) so a bypass/early return can't leak stale options.
    try {
      const internals = this.#internals
      const model = internals.model
      const relation = model.$getRelation(name) as unknown as BootedRelationLike | undefined
      const callOptions = this.#callOptions.get(name)
      const hasCallback = typeof internals.preloads[name]?.callback === 'function'

      if (
        !relation ||
        !this.#relationsCache.has(name) ||
        !CachingPreloader.#isCacheable(relation)
      ) {
        await this.#superProcessRelationForMany(name, parents, client)
        return
      }
      // See `#cachedProcessRelation`: a filtering callback bypasses; a nesting-only
      // callback caches the outer rows and cascades its captured child preloads.
      let nestedPreloads: CapturedNestedPreload[] | null = null
      if (hasCallback) {
        const callback = internals.preloads[name].callback as (query: unknown) => void
        const classification = CachingPreloader.#classifyCallback(callback)
        if (!classification.nestingOnly) {
          this.#emit('bypass', {
            model: CachingPreloader.#modelName(model),
            relation: name,
          })
          await this.#superProcessRelationForMany(name, parents, client)
          return
        }
        nestedPreloads = classification.preloads
      }

      // Resolution is an operational site (ADR-009): report + degrade to the
      // uncached batched DB load instead of failing.
      let cache: CacheServiceLike | null
      try {
        cache = await this.#resolveCache()
      } catch (cause) {
        reportRelationCacheError(
          model,
          new E_RELATION_CACHE_RESOLUTION_FAILED(
            { model: CachingPreloader.#modelName(model), relation: name },
            cause
          )
        )
        await this.#superProcessRelationForMany(name, parents, client)
        return
      }
      if (!cache) {
        await this.#superProcessRelationForMany(name, parents, client)
        return
      }

      const relatedModel = relation.relatedModel()
      const modelName = CachingPreloader.#modelName(model)
      const forceLoad = callOptions?.forceLoad === true
      // Outer related rows resolved this pass (cache-rehydrated and/or freshly
      // loaded), collected so a nesting callback can cascade onto all of them at once.
      const outerRelatedRows: LucidRow[] = []

      // Build the per-parent lookup-key list once, then issue all cache reads
      // in parallel via Promise.all so L1/L2 round-trips overlap instead of
      // serializing one per parent (which on a partially-cold cache was slower
      // than the uncached `whereIn` path it replaces).
      const parentLookupKeys: Array<{ parent: LucidRow; lookupKey: string | null }> = parents.map(
        (parent) => ({
          parent,
          lookupKey: CachingPreloader.#lookupKeyForParent(relation, parent),
        })
      )

      const cacheReads: Array<string | undefined | null> = forceLoad
        ? new Array(parentLookupKeys.length).fill(null)
        : await Promise.all(
            parentLookupKeys.map(({ lookupKey }) =>
              lookupKey
                ? // Cache read is an operational site (ADR-009): a per-parent `get`
                  // failure is reported and degraded to a miss (that parent then
                  // loads from the DB), so one bad key can't fail the whole batch.
                  cache
                    .get<string | undefined | null>({ key: lookupKey })
                    .catch((cause: unknown) => {
                      reportRelationCacheError(
                        model,
                        new E_RELATION_CACHE_READ_FAILED(
                          { model: modelName, relation: name, key: lookupKey },
                          cause
                        )
                      )
                      return null
                    })
                : Promise.resolve(null)
            )
          )

      const parentsNeedingLoad: LucidRow[] = []
      for (const [parentIndex, { parent, lookupKey }] of parentLookupKeys.entries()) {
        if (!lookupKey || forceLoad) {
          // No identity for this parent, or a forced refresh — load it.
          parentsNeedingLoad.push(parent)
          continue
        }
        const cachedEntry = cacheReads[parentIndex]
        if (cachedEntry) {
          // Decode + consume + rehydration is an operational site (ADR-009): a
          // poisoned entry is reported and degraded to a miss for this parent
          // (refetched + overwritten).
          let row: LucidRow | null = null
          try {
            const snapshot = await CachingPreloader.#consumeFromCache(
              relatedModel,
              name,
              lookupKey,
              cachedEntry
            )
            row = CachingPreloader.#rehydrate(relatedModel, snapshot, internals.sideloaded)
          } catch (cause) {
            reportRelationCacheError(
              model,
              new E_RELATION_CACHE_REHYDRATION_FAILED(
                { model: modelName, relation: name, key: lookupKey, value: cachedEntry },
                cause
              )
            )
            row = null
          }
          if (row) {
            relation.setRelated(parent, row)
            outerRelatedRows.push(row)
            // Fire-and-forget the hit log so the load path does not block on
            // logger resolution per parent.
            this.#emit('hit', { model: modelName, relation: name, key: lookupKey })
            continue
          }
          // Poisoned entry → fall through to load this parent from the DB.
        }
        parentsNeedingLoad.push(parent)
      }

      if (forceLoad) {
        this.#emit('force', { model: modelName, relation: name })
      }

      if (parentsNeedingLoad.length > 0) {
        const query = relation
          .eagerQuery(parentsNeedingLoad, client)
          .debug(internals.debugQueries)
          .sideload(internals.sideloaded)
        const result = await query.selectRelationKeys().exec()
        relation.setRelatedForMany(parentsNeedingLoad, result)

        for (const parent of parentsNeedingLoad) {
          const relatedRow = (parent.$preloaded as Record<string, LucidRow | null>)[
            relation.relationName
          ]
          if (relatedRow) {
            outerRelatedRows.push(relatedRow)
          }
          const lookupKey = CachingPreloader.#lookupKeyForParent(relation, parent)
          if (!lookupKey || !relatedRow) {
            continue
          }
          await this.#store(
            cache,
            name,
            lookupKey,
            relation,
            relatedRow,
            forceLoad ? 'force' : 'miss'
          )
        }
      }

      // Cascade the nesting callback's child preloads onto every resolved outer row
      // (hits + misses) in one batched pass, each caching at its own identity.
      if (nestedPreloads) {
        await this.#cascadeNested(relatedModel, outerRelatedRows, nestedPreloads, client)
      }
    } finally {
      this.#callOptions.delete(name)
    }
  }

  /**
   * Stores a related row's consumed-`$attributes` snapshot under its lookup key,
   * tagged with the row's canonical identity for write-through eviction.
   *
   * @param cache - The resolved cache service.
   * @param name - The relation name (for logging).
   * @param lookupKey - The canonical lookup key.
   * @param relation - The booted 1:1 relation.
   * @param relatedRow - The loaded related row.
   * @param origin - Whether the store originated from a normal miss or a forced load.
   */
  async #store(
    cache: CacheServiceLike,
    name: string,
    lookupKey: string,
    relation: BootedRelationLike,
    relatedRow: LucidRow,
    origin: 'miss' | 'force'
  ): Promise<void> {
    const tag = CachingPreloader.#identityTagForRow(relation, relatedRow)
    // Deep clone (NOT shallow): `setRelated` already handed the live related row to
    // the caller, so the snapshot must not share any nested object with it — else a
    // prepare handler mutating a nested value in place would leak into the live model
    // (ADR-012). `deepCloneSnapshot` preserves the encoder's rich leaf types.
    const snapshot = deepCloneSnapshot(relatedRow.$attributes as Record<string, unknown>)
    const entryOptions = this.#entryOptions(name)
    const modelName = CachingPreloader.#modelName(this.#internals.model)
    // Write-back is an operational site (ADR-009): a `set` failure (e.g. a
    // serialization error) is reported and SKIPPED — the row was already fetched
    // and set on the parent, so the read still succeeds; it is just not cached.
    try {
      const value = await CachingPreloader.#prepareForStore(
        relation.relatedModel(),
        name,
        lookupKey,
        snapshot
      )
      await cache.set({
        key: lookupKey,
        value,
        tags: tag ? [tag] : undefined,
        ttl: entryOptions.ttl,
        grace: entryOptions.grace,
        graceBackoff: entryOptions.graceBackoff,
        suppressL2Errors: entryOptions.suppressL2Errors,
        timeout: entryOptions.timeout,
        hardTimeout: entryOptions.hardTimeout,
        lockTimeout: entryOptions.lockTimeout,
      })
    } catch (cause) {
      reportRelationCacheError(
        this.#internals.model,
        new E_RELATION_CACHE_WRITE_FAILED(
          { model: modelName, relation: name, key: lookupKey, tag, value: snapshot },
          cause
        )
      )
      return
    }
    this.#emit('store', {
      model: modelName,
      relation: name,
      key: lookupKey,
      tag,
      ttl: entryOptions.ttl,
    })
    if (origin === 'miss') {
      this.#emit('miss', {
        model: modelName,
        relation: name,
        key: lookupKey,
      })
    }
  }

  /**
   * Cascades captured nested preloads onto already-resolved related rows so each
   * nested 1:1 level caches at its own identity (AC line 33 / ADR-002). Drives a
   * sibling {@link CachingPreloader} for the related model, which dispatches each
   * nested relation through the SAME cache logic (recursing for deeper nesting)
   * while batching the load across all rows via `processAllForMany`.
   *
   * Nested relations that are not cache-configured (or not a cacheable 1:1) simply
   * load normally through that preloader's own bypass — identical to native Lucid.
   * A nested *filtering* callback is re-classified and bypassed by the sibling
   * preloader in turn, so each level decides independently.
   *
   * @param relatedModel - The model of the resolved related rows.
   * @param rows - The resolved related rows (cache-rehydrated and/or freshly loaded).
   * @param nested - The child preloads captured from the (nesting-only) callback.
   * @param client - The query client to use for nested eager queries.
   */
  async #cascadeNested(
    relatedModel: LucidModel,
    rows: LucidRow[],
    nested: CapturedNestedPreload[],
    client: unknown
  ): Promise<void> {
    if (rows.length === 0 || nested.length === 0) {
      return
    }
    const relationsCache =
      (
        relatedModel as LucidModel & {
          $resourcefulRelationsCache?: Map<string, RelationCacheEntryOptions>
        }
      ).$resourcefulRelationsCache ?? new Map<string, RelationCacheEntryOptions>()
    const driver = new CachingPreloader(
      relatedModel,
      relationsCache
    ) as unknown as NestedCascadeDriver
    driver.sideload({ ...this.#internals.sideloaded })
    for (const { name, callback } of nested) {
      driver.preload(name, callback)
    }
    await driver.processAllForMany(rows, client)
  }

  /**
   * Records per-call cache controls for a relation (used by the builder method
   * `preloadCached`) so the next `processRelation`/`processRelationForMany`
   * applies them.
   *
   * @param name - The relation name.
   * @param options - The per-call cache controls.
   */
  recordCallOptions(name: string, options: RelationCacheCallOptions): void {
    this.#callOptions.set(name, options)
  }

  /**
   * Clones the preloader, preserving its concrete type and cache configuration
   * so cloned queries (e.g. pagination's count clone) keep caching by
   * construction (ADR-001). Uses `this.constructor` so further subclasses clone
   * to their own type rather than silently degrading to the base.
   *
   * @returns A new preloader of the same concrete type carrying the same state.
   */
  clone(): Preloader {
    const internals = this.#internals
    const Ctor = this.constructor as new (
      model: LucidModel,
      relationsCache: Map<string, RelationCacheEntryOptions>,
      callOptions?: Map<string, RelationCacheCallOptions>
    ) => CachingPreloader
    const cloned = new Ctor(internals.model, this.#relationsCache, new Map(this.#callOptions))
    const clonedInternals = cloned as unknown as BasePreloaderInternals
    clonedInternals.preloads = Object.assign({}, internals.preloads)
    clonedInternals.sideloaded = Object.assign({}, internals.sideloaded)
    clonedInternals.debugQueries = internals.debugQueries
    return cloned
  }
}

/**
 * Registers a model's configured cacheable 1:1 relations as process-global cache
 * TARGETS, independent of ever constructing a {@link CachingPreloader} for it.
 *
 * The host application calls this at boot for every model that declares a
 * `relationsCache`, so the write-through eviction gate
 * (`isRelationCacheTarget`) is correct in EVERY process — including a "cold"
 * worker (e.g. a rates-sync command) that only writes a target row (`Currency`)
 * and never queries the declaring parent (`PaymentIntentTicket`). Without this,
 * such a process would skip `deleteByTag` and leave stale entries in a shared L2
 * for other processes until the TTL elapsed.
 *
 * Booting the model's relations + validating belongsTo key consistency happens
 * here too, so a misconfiguration fails loud at boot rather than at first load.
 *
 * @param model - A model that may declare a `relationsCache`.
 * @public
 */
export const registerRelationCacheTargetsForModel = (model: LucidModel): void => {
  CachingPreloader.registerCacheTargets(model)
}
